import type {
  IncidentListSortKey,
  IncidentStatus,
  PlanSizeTier,
  Severity,
  SortDirection,
} from '@prerender/diagnostics-api';

import type { IncidentListParams } from './incidentListParams';

export const INCIDENT_LIST_PAGE_SIZE = 50;

export type IncidentListQueryArgs = {
  page: number;
  pageSize: number;
  sortBy?: IncidentListSortKey;
  sortDirection?: SortDirection;
  searchQuery?: string;
  incidentType?: string[];
  crawler?: string[];
  severity?: Severity[];
  status?: IncidentStatus[];
  planTier?: PlanSizeTier[];
  dateStart?: string;
  dateEnd?: string;
};

const localDayStart = (day: string): Date => {
  const [year, month, dayOfMonth] = day.split('-').map(Number);

  return new Date(year, month - 1, dayOfMonth);
};

const startOfDayInstant = (day: string | null): string | undefined =>
  day === null ? undefined : localDayStart(day).toISOString();

const endOfDayInstant = (day: string | null): string | undefined => {
  if (day === null) return undefined;
  const start = localDayStart(day);
  const nextDayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);

  return new Date(nextDayStart.getTime() - 1).toISOString();
};

const nonEmpty = <T>(values: T[]): T[] | undefined => (values.length === 0 ? undefined : values);

export function toIncidentListQueryArgs(params: IncidentListParams): IncidentListQueryArgs {
  return {
    page: params.page,
    pageSize: INCIDENT_LIST_PAGE_SIZE,
    sortBy: params.sort?.key,
    sortDirection: params.sort?.direction,
    searchQuery: params.search === '' ? undefined : params.search,
    incidentType: nonEmpty(params.incidentTypes),
    crawler: nonEmpty(params.crawlers),
    severity: nonEmpty(params.severities),
    status: nonEmpty(params.statuses),
    planTier: nonEmpty(params.planTiers),
    dateStart: startOfDayInstant(params.dateStart),
    dateEnd: endOfDayInstant(params.dateEnd),
  };
}

const appendEach = (query: URLSearchParams, key: string, values: readonly string[] | undefined): void => {
  (values ?? []).forEach((value) => query.append(key, value));
};

const appendIfSet = (query: URLSearchParams, key: string, value: string | undefined): void => {
  if (value !== undefined) query.append(key, value);
};

export function incidentListQueryString(args: IncidentListQueryArgs): string {
  const query = new URLSearchParams();

  query.append('page', String(args.page));
  query.append('pageSize', String(args.pageSize));
  appendIfSet(query, 'sortBy', args.sortBy);
  appendIfSet(query, 'sortDirection', args.sortDirection);
  appendIfSet(query, 'searchQuery', args.searchQuery);
  appendEach(query, 'incidentType', args.incidentType);
  appendEach(query, 'crawler', args.crawler);
  appendEach(query, 'severity', args.severity);
  appendEach(query, 'status', args.status);
  appendEach(query, 'planTier', args.planTier);
  appendIfSet(query, 'dateStart', args.dateStart);
  appendIfSet(query, 'dateEnd', args.dateEnd);

  return query.toString();
}
